import React, { useEffect, useState } from "react";
import Dialog from "@mui/material/Dialog";
import { Form, Formik, FormikHelpers } from "formik";
import { array, object, string } from "yup";
import { FacultyFormValues } from "src/models/Faculty.model";
import Faculty from "../Layouts/FacultyForm";
import {
  useGetFacultyByIdQuery,
  useUpdateFacultyByIdMutation,
} from "src/services/FacultyService";
import { showToast } from "src/utils/showToaster";
import { CircularProgress } from "@mui/material";

type Props = {
  onClose: () => void;
  selectedFacultyId: string;
};

const EditFacultyWrapper = ({ onClose, selectedFacultyId }: Props) => {
  // Form Initial Values
  const [items, setItems] = useState<any>({});

  const { isLoading, isFetching, data } =
    useGetFacultyByIdQuery(selectedFacultyId);
  const [updateFacultyById] = useUpdateFacultyByIdMutation();

  useEffect(() => {
    if (!isLoading && !isFetching) {
      setItems(data?.data);
    }
  }, [isLoading, isFetching, data]);

  const initialValues: FacultyFormValues = {
    facultyName: items?.facultyName || "",
    facultyCode: items?.facultyCode || "",
    facultyImage: items?.facultyImage || "",
    facultyDescription: items?.facultyDescription || "",
    rank: items?.rank || "",
    institute: items?.institute?.map((ins: any) => ({
      label: ins.instituteName,
      value: ins.instituteId,
    })),
  };
  // Validation Schema
  const validationSchema = object().shape({
    facultyName: string().required("Please enter faculty name"),

    institute: array()
      .of(object().required("please select institute"))
      .min(1, "Please select at least one institute")
      .required("Please select institute name"),

    facultyCode: string().required("Please enter faculty code"),
  });
  // Handle Submit
  const handleSubmit = (
    values: FacultyFormValues,
    { setSubmitting, resetForm }: FormikHelpers<FacultyFormValues>
  ) => {
    const payload = {
      facultyName: values.facultyName,
      facultyCode: values.facultyCode,
      facultyImage: values.facultyImage,
      facultyDescription: values.facultyDescription,
      rank: values.rank,
      institute: values.institute.map((ins) => ins.value),
    };
    updateFacultyById({ id: selectedFacultyId, body: payload }).then(
      (res: any) => {
        if (res?.error) {
          showToast("error", res?.error?.data?.message);
        } else {
          if (res?.data?.status) {
            showToast("success", res?.data?.message);
            resetForm();
            onClose();
          } else {
            showToast("error", res?.data?.message);
          }
        }
        setSubmitting(false);
      }
    );
  };

  return (
    <Dialog open maxWidth="xs" fullWidth>
      <Formik
        initialValues={initialValues}
        enableReinitialize
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {(formikProps) => (
          <Form>
            {(isLoading || isFetching) && (
              <div className="absolute w-[100%] h-[100%] flex justify-center items-center z-10 bg-slate-100 opacity-50">
                <CircularProgress />
              </div>
            )}
            <Faculty
              formikProps={formikProps}
              onClose={onClose}
              formType="EDIT"
            />
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export default EditFacultyWrapper;
